import { useState, type CSSProperties, type FormEvent } from "react";
import { ArrowRight, Eye, EyeOff, LockKeyhole, MonitorPlay, ShieldCheck } from "lucide-react";
import { api, ApiError, login, type AccessMode, type Session } from "./api.js";
import type { PlatformBranding } from "./adminOperations.js";

interface LoginPageProps {
  branding: PlatformBranding;
  mode: AccessMode;
  onAuthenticated(session: Session): void;
}

export function LoginPage({ branding, mode, onAuthenticated }: LoginPageProps) {
  const isAdmin = mode === "admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      onAuthenticated(await login(email, password, mode));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Unable to sign in.");
    } finally {
      setPending(false);
    }
  }

  async function requestReset(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      await api("/api/v1/auth/password-reset/request", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setResetSent(true);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Unable to request a reset.");
    } finally {
      setPending(false);
    }
  }

  return (
    <main
      className={`login-page login-page--${mode}`}
      style={
        {
          "--brand-primary": branding.primaryColor,
          "--brand-secondary": branding.secondaryColor,
        } as CSSProperties
      }
    >
      <a className="login-skip-link" href="#login-form">
        Skip to sign in
      </a>
      <section className="login-story" aria-label={`${branding.name} access introduction`}>
        <div className="login-story__header">
          {isAdmin ? (
            <div className="brand brand--light">
              {branding.logoUrl ? (
                <img className="brand-logo" src={branding.logoUrl} alt="" />
              ) : (
                <span className="brand-mark">
                  <MonitorPlay size={23} />
                </span>
              )}
              <span>{branding.name}</span>
            </div>
          ) : (
            <a className="brand brand--light" href="/" aria-label={`${branding.name} home`}>
              {branding.logoUrl ? (
                <img className="brand-logo" src={branding.logoUrl} alt="" />
              ) : (
                <span className="brand-mark">
                  <MonitorPlay size={23} />
                </span>
              )}
              <span>{branding.name}</span>
            </a>
          )}
          <span className="login-access-label">
            {isAdmin ? <ShieldCheck size={15} /> : <MonitorPlay size={15} />}
            {isAdmin ? "Operator access" : "Client access"}
          </span>
        </div>
        <div className="story-copy">
          <span className="eyebrow eyebrow--light">
            {isAdmin ? "Platform administration" : "Display management"}
          </span>
          <h1>{isAdmin ? "Platform control, kept separate." : "Keep every screen in step."}</h1>
          <p>
            {isAdmin
              ? `Manage ${branding.name} operations through the dedicated administrator entrance.`
              : "Publish content, organize playlists, and monitor your displays from one focused workspace."}
          </p>
        </div>
        <div className="login-story__note">
          {isAdmin ? <LockKeyhole size={19} /> : <ShieldCheck size={19} />}
          <p>
            <strong>{isAdmin ? "Restricted platform entry" : "Private customer workspace"}</strong>
            <span>
              {isAdmin
                ? "Only authorized platform operators can continue."
                : "Your account opens only your organization’s content and screens."}
            </span>
          </p>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <div className="login-card__heading">
            <span className="login-card__role-icon" aria-hidden="true">
              {isAdmin ? <ShieldCheck size={20} /> : <MonitorPlay size={20} />}
            </span>
            <span className="eyebrow">{isAdmin ? "Administrator portal" : "Client workspace"}</span>
            <h2 id="login-heading">
              {isAdmin
                ? "Administrator sign in"
                : resetOpen
                  ? "Reset your password"
                  : "Welcome back"}
            </h2>
            <p className="muted">
              {isAdmin
                ? `Sign in to manage the ${branding.name} platform.`
                : resetOpen
                  ? "Enter your client email address to receive a secure reset link."
                  : "Sign in to manage your screens and content."}
            </p>
          </div>

          {new URLSearchParams(window.location.search).has("passwordChanged") ? (
            <div className="notice notice--good">
              <ShieldCheck size={18} />
              <span>Password changed. Sign in again with the new password.</span>
            </div>
          ) : null}
          {isAdmin && new URLSearchParams(window.location.search).has("emailChanged") ? (
            <div className="notice notice--good">
              <ShieldCheck size={18} />
              <span>Email changed. Sign in again with the new administrator email.</span>
            </div>
          ) : null}
          <form
            id="login-form"
            onSubmit={(event) => void (resetOpen ? requestReset(event) : submit(event))}
            className="form-stack"
            aria-labelledby="login-heading"
            aria-busy={pending}
            tabIndex={-1}
          >
            <label htmlFor="login-email">
              <span>Email address</span>
              <input
                id="login-email"
                type="email"
                value={email}
                autoComplete={`${isAdmin ? "section-qassist-admin" : "section-qassist-client"} username`}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
            {!resetOpen ? (
              <div className="login-password-field">
                <label id="login-password-label" htmlFor="login-password">
                  Password
                </label>
                <div className="login-password-control">
                  <input
                    id="login-password"
                    type={passwordVisible ? "text" : "password"}
                    value={password}
                    autoComplete={`${isAdmin ? "section-qassist-admin" : "section-qassist-client"} current-password`}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    aria-label={passwordVisible ? "Conceal" : "Reveal"}
                    aria-describedby="login-password-label"
                    aria-pressed={passwordVisible}
                    onClick={() => setPasswordVisible((value) => !value)}
                  >
                    {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            ) : null}
            {error ? (
              <div className="form-error" role="alert">
                {error}
              </div>
            ) : null}
            <button
              className="button button--primary button--wide"
              type="submit"
              disabled={pending}
            >
              {pending
                ? resetOpen
                  ? "Sending…"
                  : "Signing in…"
                : resetOpen
                  ? "Send reset email"
                  : "Open workspace"}{" "}
              <ArrowRight size={17} />
            </button>
          </form>
          {resetSent ? (
            <div className="notice notice--good">
              <ShieldCheck size={18} />
              <span>If that client account exists, a reset email has been sent.</span>
            </div>
          ) : null}
          {!isAdmin ? (
            <button
              type="button"
              className="text-button login-reset-link"
              onClick={() => {
                setResetOpen((value) => !value);
                setResetSent(false);
                setError("");
              }}
            >
              {resetOpen ? "Back to sign in" : "Forgot password?"}
            </button>
          ) : null}
          <div className="security-note">
            <LockKeyhole size={16} aria-hidden="true" />
            <p>
              {isAdmin
                ? "Administrative access is restricted to authorized platform operators."
                : "Dashboard access is separate from device activation. TV credentials are never placed in player URLs."}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
